// @safetyqube/sq-ai-client — conversation state machine (framework-agnostic).
//
// Relocated VERBATIM from the testing UI's useSqAiConversation.js (Phase 2 — a
// move, not a rewrite). No React here. The reducer + helpers reduce the streamed
// `ai:*` events into renderable per-turn state; `eventToActions` is the exact
// event→action mapping the UI hook used; `buildTurnEmit`/`cancelEmit` are its
// outbound emit shapes. The React bindings (@safetyqube/sq-ai-react) wrap these.

export const initial = (id) => ({ id, messages: [], status: "idle" }); // idle | in_flight

export function updateLastAssistant(state, updater) {
  const messages = [...state.messages];
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") return state;
  messages[messages.length - 1] = updater(last);
  return { ...state, messages };
}

// Freeze any still-"live" thinking step → "done" (so it stops animating when the
// turn settles, even if no explicit thinking 'done' event arrived).
export function freezeThinking(steps) {
  return (steps || []).map((s) => (s.status === "live" ? { ...s, status: "done" } : s));
}

export function reducer(state, action) {
  switch (action.type) {
    case "reset":
      return initial(action.id);

    case "send":
      // Append the user turn and an assistant placeholder in one step so
      // every subsequent event lands on a live assistant message.
      return {
        ...state,
        status: "in_flight",
        messages: [
          ...state.messages,
          { id: `u-${action.ts}`, role: "user", text: action.text },
          {
            id: `a-${action.ts}`,
            role: "assistant",
            assumptions: [],
            charts: [],
            caption: "",
            done: false,
            error: null,
            durationMs: null,
            sqls: [], // dev-only: generated SQL per query tool call (Fix 3 accordion)
            thinkingSteps: [], // streaming "Thinking…" phase/update lines (ai:thinking)
            thinkingTick: 0, // bumps on every thinking event so the UI gap-clock resets reliably
            suggestions: null, // starter cards from a greeting short-circuit (ai:done.suggestions)
            resultMeta: null, // #2a: window / date basis / modules caption (ai:done.resultMeta)
            exports: null, // CSV export descriptors (ai:done.exports; absent on non-data turns) — survives refresh with the message state
            costDisplay: null, // dev-only token/cost block (ai:done.costDisplay). ABSENT unless the server opted in — the server omits what is switched off, so null here means "not shown", never "not measured"
          },
        ],
      };

    case "assumption":
      return updateLastAssistant(state, (m) => ({
        ...m,
        assumptions: [...m.assumptions, { text: action.text, rationale: action.rationale || null }],
      }));

    case "sql":
      // Dev-only: the agent surfaces generated SQL on ai:tool_call (uiMode dev).
      // De-dupe by tool_use_id so a started/completed pair doesn't double-list.
      return updateLastAssistant(state, (m) => {
        const sqls = m.sqls || [];
        if (sqls.some((s) => s.id === action.id)) return m;
        return { ...m, sqls: [...sqls, { id: action.id, name: action.name, sql: action.sql }] };
      });

    case "chart":
      return updateLastAssistant(state, (m) => ({ ...m, charts: [...m.charts, action.spec] }));

    case "caption":
      return updateLastAssistant(state, (m) => ({ ...m, caption: (m.caption || "") + action.text }));

    case "thinking_phase":
      // New real step. Freeze the current live line, then push a new live one.
      return updateLastAssistant(state, (m) => {
        if (m.done) return m;
        const frozen = freezeThinking(m.thinkingSteps);
        return {
          ...m,
          thinkingTick: (m.thinkingTick || 0) + 1,
          thinkingSteps: [
            ...frozen,
            { id: `t-${action.ts}-${frozen.length}`, icon: action.icon || null, phrase: action.phrase || "", status: "live" },
          ],
        };
      });

    case "thinking_update":
      // Sub-progress within the current step — replace the active line in place.
      return updateLastAssistant(state, (m) => {
        const steps = m.thinkingSteps || [];
        let i = -1;
        for (let k = steps.length - 1; k >= 0; k--) {
          if (steps[k].status === "live") { i = k; break; }
        }
        if (i < 0) return m;
        const next = steps.slice();
        next[i] = { ...next[i], phrase: action.phrase || next[i].phrase };
        // Bump the tick even when the phrase is byte-identical, so the UI's
        // gap-fill clock resets on every real event (an update IS an event).
        return { ...m, thinkingSteps: next, thinkingTick: (m.thinkingTick || 0) + 1 };
      });

    case "thinking_done":
      return updateLastAssistant(state, (m) => ({
        ...m,
        thinkingSteps: freezeThinking(m.thinkingSteps),
        thinkingTick: (m.thinkingTick || 0) + 1,
      }));

    case "done":
      return {
        ...updateLastAssistant(state, (m) => ({
          ...m,
          done: true,
          durationMs: action.durationMs || null,
          suggestions: Array.isArray(action.suggestions) && action.suggestions.length ? action.suggestions : null,
          resultMeta: action.resultMeta || null,
          exports: Array.isArray(action.exports) && action.exports.length ? action.exports : null,
          costDisplay: action.costDisplay || null,
          // Data-turn caption guard (Option A): the server strips a raw markdown-table / HTML /
          // CSV artifact from the caption and ships the cleaned text as finalText. Swap it in
          // over the streamed caption. Present ONLY when something was stripped, so an ordinary
          // turn (no finalText) keeps its streamed caption byte-identical.
          caption: typeof action.finalText === "string" ? action.finalText : m.caption,
          thinkingSteps: freezeThinking(m.thinkingSteps),
        })),
        status: "idle",
      };

    case "error":
      return {
        ...updateLastAssistant(state, (m) => ({
          ...m,
          done: true,
          error: { code: action.code, ...(action.status ? { status: action.status } : {}), message: action.message },
          thinkingSteps: freezeThinking(m.thinkingSteps),
        })),
        status: "idle",
      };

    default:
      return state;
  }
}

// The streamed events the conversation state machine consumes. (ai:context_update
// is intentionally NOT here — it is a picker concern handled by the consumer UI,
// exactly as in the original hook. ai:stage / ai:progress_text are intentionally
// ignored — the streaming ai:thinking status supersedes the old single status line.)
export const STREAMED_EVENTS = [
  "ai:assumption",
  "ai:chart",
  "ai:text_delta",
  "ai:thinking",
  "ai:tool_call",
  "ai:clarification",
  "ai:done",
  "ai:error",
];

// Map a streamed socket event (name + payload) to zero or more reducer actions
// for the given conversationId. Verbatim from the UI hook's `handlers` map:
// guards on conversationId, fans ai:thinking out by p.type, and promotes
// ai:clarification into a caption (chart mode writes clarifications as prose, but
// if the model still calls ask_clarifying_question the structured payload must
// still render — otherwise the turn looks like a dead end).
export function eventToActions(eventName, p, conversationId) {
  if (!p || p.conversationId !== conversationId) return [];
  switch (eventName) {
    case "ai:assumption":
      return [{ type: "assumption", text: p.assumption, rationale: p.rationale }];
    case "ai:chart":
      return [{ type: "chart", spec: p.spec }];
    case "ai:text_delta":
      return [{ type: "caption", text: p.text || "" }];
    case "ai:thinking":
      if (p.type === "phase") return [{ type: "thinking_phase", icon: p.icon, phrase: p.phrase, ts: Date.now() }];
      if (p.type === "update") return [{ type: "thinking_update", phrase: p.phrase }];
      if (p.type === "done") return [{ type: "thinking_done" }];
      return [];
    case "ai:tool_call":
      // Dev-only: collect generated SQL for the SQL accordion (uiMode dev / devSql).
      return p.executedSql ? [{ type: "sql", id: p.tool_use_id, name: p.name, sql: p.executedSql }] : [];
    case "ai:clarification": {
      const lines = [];
      if (p.question) lines.push(p.question);
      if (Array.isArray(p.options) && p.options.length > 0) {
        for (const opt of p.options) {
          const label = typeof opt === "string" ? opt : (opt && (opt.label || opt.value));
          if (label) lines.push(`- ${label}`);
        }
      }
      const text = lines.join("\n");
      return text ? [{ type: "caption", text }] : [];
    }
    case "ai:done":
      return [{ type: "done", durationMs: p.durationMs, suggestions: p.suggestions, resultMeta: p.resultMeta, exports: p.exports, finalText: p.finalText, costDisplay: p.costDisplay }];
    case "ai:error":
      // `status` rides only an expired session's refusal (contract ErrorEvent), kept so state matches connectionError.
      return [{ type: "error", code: p.code, ...(p.status ? { status: p.status } : {}), message: p.message }];
    default:
      return [];
  }
}

// Build the outbound emit for a turn. The first message opens the conversation via
// ai:chart_query (the server locks mode + provider to the conversation then);
// every later message is a follow-up via ai:user_message on the SAME
// conversationId, so the agent keeps context across turns.
//
// `authToken` is the caller's CURRENT session token. It rides every turn because the
// handshake's copy is frozen at connect, and a 24-hour token outlives that: the server
// re-verifies this one and refreshes privileges from it, so a grant or a revoke lands
// without a reconnect. It is attached only when there IS one — a caller that supplies no
// token emits the byte-identical payload it emitted before this existed, and the server
// falls back to the handshake identity.
//
// `extras` rides the OPENING turn only, spread last — the compare screen's per-pane model override
// travels this way, and the server reads it on ai:chart_query and nowhere else. A caller that passes
// none emits the byte-identical payload.
export function buildTurnEmit({ isFirst, conversationId, text, tenantSchema, mode = "chart", context, authToken, extras }) {
  const auth = authToken ? { authToken } : null;
  if (isFirst) {
    return {
      event: "ai:chart_query",
      payload: { conversationId, queryText: text, tenantSchema, provider: "llm-agent", mode, context, ...auth, ...extras },
    };
  }
  return {
    event: "ai:user_message",
    payload: { conversationId, text, tenantSchema, provider: "llm-agent", context, ...auth },
  };
}

export function cancelEmit(conversationId) {
  return { event: "ai:cancel", payload: { conversationId } };
}
